import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WalletTransfer } from '@/components/WalletTransfer';
import {
  TrendingUp, ArrowUpRight, ArrowDownRight, Loader2,
  RefreshCw, Wallet, ChevronLeft, Search, Activity, Star
} from 'lucide-react';

const fmtMoney = (n, d = 2) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d })}`;
const fmtPrice = (p) => {
  const n = Number(p || 0);
  if (n === 0) return '—';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  if (n >= 0.01) return n.toFixed(4);
  return n.toFixed(6);
};

export default function TradingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isInvestor = user?.role === 'investor';
  const isAdmin = ['super_admin', 'admin', 'manager', 'trader'].includes(user?.role);

  const [assets, setAssets] = useState([]);
  const [prices, setPrices] = useState({});
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');
  const [search, setSearch] = useState('');
  const [positions, setPositions] = useState(null);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientBalance, setClientBalance] = useState(0);
  const [clientName, setClientName] = useState('');
  const [clientCode, setClientCode] = useState('');
  const [tradeForm, setTradeForm] = useState({ side: 'buy', quantity: '' });
  const [executing, setExecuting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trading_favs') || '["BTC","ETH","XAUUSD"]'); } catch { return ['BTC','ETH','XAUUSD']; }
  });
  const chartRef = useRef(null);

  // ─── Endpoints (role-aware) ───
  const ep = useMemo(() => ({
    positions: isInvestor ? '/trading/me/positions' : (cid) => `/trading/positions/${cid}`,
    history: isInvestor ? '/trading/me/history' : (cid) => `/trading/history/${cid}`,
    execute: isInvestor ? '/trading/me/execute' : '/trading/execute',
    info: isInvestor ? '/trading/me/info' : null,
  }), [isInvestor]);

  const fetchPrices = useCallback(async () => {
    try {
      const { data } = await api.get('/trading/prices');
      setPrices(data);
    } catch (err) {
      console.error('[Trading] fetchPrices failed, keeping cached prices:', err?.message || err);
    }
  }, []);

  const refreshBalanceFor = useCallback(async (cid) => {
    if (isInvestor) {
      try {
        const { data } = await api.get('/trading/me/info');
        setSelectedClient(data.client_id);
        setClientBalance(data.current_balance || 0);
        setClientName(data.name || '');
        setClientCode(data.client_code || '');
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || 'No client profile linked');
      }
    } else if (cid) {
      try {
        const { data } = await api.get(`/clients/${cid}`);
        setClientBalance(data.current_balance || 0);
        setClientName(data.name || '');
        setClientCode(data.client_id || '');
      } catch (err) {
        console.error('[Trading] failed to refresh client balance:', err?.message || err);
      }
    }
  }, [isInvestor]);

  const fetchData = useCallback(async () => {
    try {
      const [aRes, pRes] = await Promise.all([api.get('/trading/assets'), api.get('/trading/prices')]);
      setAssets(aRes.data);
      setPrices(pRes.data);
      if (isInvestor) {
        await refreshBalanceFor(null);
      } else if (isAdmin) {
        const cRes = await api.get('/clients').catch(() => ({ data: [] }));
        setClients(cRes.data);
        if (cRes.data.length > 0) {
          const first = cRes.data[0];
          setSelectedClient(first.id);
          setClientBalance(first.current_balance || 0);
          setClientName(first.name || '');
          setClientCode(first.client_id || '');
        }
      }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isAdmin, isInvestor, refreshBalanceFor]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh prices every 10s for near-real-time updates
  useEffect(() => {
    const t = setInterval(fetchPrices, 10000);
    return () => clearInterval(t);
  }, [fetchPrices]);

  // Load positions/history when client changes (or always for investor)
  const reloadAccount = useCallback(async () => {
    if (!selectedClient && !isInvestor) return;
    const posUrl = isInvestor ? ep.positions : ep.positions(selectedClient);
    const histUrl = isInvestor ? ep.history : ep.history(selectedClient);
    const [posRes, histRes] = await Promise.all([
      api.get(posUrl).catch(() => ({ data: null })),
      api.get(histUrl).catch(() => ({ data: [] })),
    ]);
    setPositions(posRes.data);
    setTradeHistory(histRes.data || []);
  }, [selectedClient, isInvestor, ep]);

  useEffect(() => { reloadAccount(); }, [reloadAccount, prices]);

  // Admin: when changing client dropdown
  useEffect(() => {
    if (!isInvestor && selectedClient) refreshBalanceFor(selectedClient);
  }, [selectedClient, isInvestor, refreshBalanceFor]);

  // TradingView Widget
  useEffect(() => {
    const container = chartRef.current;
    if (!container) return;
    // Safe DOM clear (no innerHTML — avoids XSS surface)
    while (container.firstChild) container.removeChild(container.firstChild);
    const tvSymbol = selectedSymbol === 'XAUUSD' ? 'OANDA:XAUUSD'
      : selectedSymbol === 'XAGUSD' ? 'OANDA:XAGUSD'
      : selectedSymbol === 'USOIL' ? 'TVC:USOIL'
      : selectedSymbol === 'NGAS' ? 'NYMEX:NG1!'
      : `BINANCE:${selectedSymbol}USDT`;
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    // textContent (never parsed as HTML) is safe for JSON config strings
    script.textContent = JSON.stringify({
      autosize: true, symbol: tvSymbol, interval: "15", timezone: "Asia/Kolkata",
      theme: "light", style: "1", locale: "en", hide_top_toolbar: false,
      hide_legend: false, allow_symbol_change: false, save_image: false,
      calendar: false, hide_volume: false,
      support_host: "https://www.tradingview.com",
      backgroundColor: "rgba(255, 255, 255, 1)", gridColor: "rgba(203, 213, 225, 0.3)",
    });
    const c = document.createElement('div');
    c.className = 'tradingview-widget-container__widget';
    c.style.height = '100%';
    c.style.width = '100%';
    container.appendChild(c);
    container.appendChild(script);
    return () => { while (container.firstChild) container.removeChild(container.firstChild); };
  }, [selectedSymbol]);

  const handleTrade = async () => {
    if (!selectedClient && !isInvestor) { toast.error('Select a client'); return; }
    if (!tradeForm.quantity || parseFloat(tradeForm.quantity) <= 0) { toast.error('Enter quantity'); return; }
    setExecuting(true);
    try {
      const payload = isInvestor
        ? { symbol: selectedSymbol, side: tradeForm.side, quantity: parseFloat(tradeForm.quantity) }
        : { client_id: selectedClient, symbol: selectedSymbol, side: tradeForm.side, quantity: parseFloat(tradeForm.quantity) };
      const { data } = await api.post(ep.execute, payload);
      toast.success(`${data.side.toUpperCase()} ${data.quantity} ${data.symbol} @ $${Number(data.entry_price).toFixed(4)}`);
      setTradeForm({ ...tradeForm, quantity: '' });
      await Promise.all([refreshBalanceFor(selectedClient), reloadAccount()]);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setExecuting(false); }
  };

  const closePosition = async (p) => {
    setExecuting(true);
    try {
      const payload = isInvestor
        ? { symbol: p.symbol, side: 'sell', quantity: p.quantity }
        : { client_id: selectedClient, symbol: p.symbol, side: 'sell', quantity: p.quantity };
      await api.post(ep.execute, payload);
      toast.success(`Closed ${p.symbol} position`);
      await Promise.all([refreshBalanceFor(selectedClient), reloadAccount()]);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setExecuting(false); }
  };

  const toggleFav = (sym) => {
    const next = favs.includes(sym) ? favs.filter(s => s !== sym) : [...favs, sym];
    setFavs(next);
    localStorage.setItem('trading_favs', JSON.stringify(next));
  };

  const currentPrice = prices[selectedSymbol]?.price || 0;
  const change24h = prices[selectedSymbol]?.change_24h || 0;
  const qty = parseFloat(tradeForm.quantity) || 0;
  const tradeValue = qty * currentPrice;
  const canAfford = tradeForm.side === 'sell' || tradeValue <= clientBalance;

  const filteredAssets = (type) => {
    const term = search.toLowerCase().trim();
    return assets.filter(a => a.type === type && (!term || a.symbol.toLowerCase().includes(term) || a.name.toLowerCase().includes(term)));
  };
  const cryptoAssets = filteredAssets('crypto');
  const commodityAssets = filteredAssets('commodity');
  const favAssets = assets.filter(a => {
    if (!favs.includes(a.symbol)) return false;
    const term = search.toLowerCase().trim();
    return !term || a.symbol.toLowerCase().includes(term) || a.name.toLowerCase().includes(term);
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={24} className="animate-spin text-brand-primary" /></div>;

  // Empty state for investor without linked client (or admin viewing /investor/trading)
  if ((isInvestor || (!isAdmin && !selectedClient)) && !selectedClient) {
    return (
      <div className="min-h-screen bg-brand-bg p-4 sm:p-6" data-testid="trading-page">
        <div className="max-w-2xl mx-auto pt-16">
          <Card className="border border-brand-border bg-white">
            <CardContent className="p-10 text-center">
              <Wallet className="mx-auto text-brand-text-secondary mb-4" size={40} />
              <h2 className="font-heading text-xl font-bold text-brand-text-primary mb-2">No Client Profile Linked</h2>
              <p className="text-sm text-brand-text-secondary mb-6">Your account is not linked to a client profile yet. Please contact your administrator to enable Spot Trading.</p>
              <Button onClick={() => navigate(isInvestor ? '/investor' : '/dashboard')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg" data-testid="empty-back-btn">
                <ChevronLeft size={14} className="mr-1" /> Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const Header = () => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-3">
        {isInvestor && (
          <Button variant="outline" size="sm" onClick={() => navigate('/investor')} className="rounded-lg border-brand-border h-9" data-testid="back-to-portal-btn">
            <ChevronLeft size={14} className="mr-1" /> Portal
          </Button>
        )}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight flex items-center gap-2">
            <Activity className="text-brand-primary" size={24} /> Spot Trading
          </h1>
          <p className="text-xs text-brand-text-secondary mt-0.5">Live markets · Crypto & Commodities</p>
        </div>
      </div>
      <div className="flex gap-2 items-center">
        {!isInvestor && isAdmin && (
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-56 bg-white border-brand-border rounded-lg text-sm h-9" data-testid="trading-client-select">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_id} — {c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {isInvestor && <WalletTransfer triggerLabel="Transfer" onDone={() => refreshBalanceFor(selectedClient)} />}
        <Button variant="outline" size="icon" onClick={fetchPrices} className="h-9 w-9 rounded-lg" data-testid="refresh-prices-btn"><RefreshCw size={15} /></Button>
      </div>
    </div>
  );

  return (
    <div className={`${isInvestor ? 'min-h-screen bg-brand-bg p-4 sm:p-6' : 'space-y-4'}`} data-testid="trading-page">
      <div className={isInvestor ? 'max-w-[1600px] mx-auto' : ''}>
        <Header />

        {/* Real-fund connection notice */}
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-brand-primary/5 border border-brand-primary/20" data-testid="fund-connect-notice">
          <div className="w-1.5 h-1.5 mt-1.5 rounded-full bg-brand-primary animate-pulse flex-shrink-0" />
          <p className="text-[11px] text-brand-text-secondary leading-relaxed">
            <span className="font-semibold text-brand-text-primary">Connected to your real fund.</span> Every BUY deducts from your Available Funds. Every SELL credits sale proceeds, and the realized profit/loss is automatically posted to your Investor Portal dashboard.
          </p>
        </div>

        {/* Stats row: Balance + Portfolio + Today's PnL */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Card className="bg-gradient-to-br from-brand-primary to-brand-primary/80 text-white border-0 shadow-sm" data-testid="client-fund-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-semibold">Available Funds</p>
                <Wallet size={16} className="opacity-90" />
              </div>
              <p className="font-heading text-2xl font-bold tracking-tight">{fmtMoney(clientBalance)}</p>
              <p className="text-[10px] opacity-80 mt-1">{clientCode ? `${clientCode} · ${clientName}` : clientName || '—'}</p>
            </CardContent>
          </Card>
          <Card className="border border-brand-border bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Portfolio Value</p>
                <TrendingUp size={16} className="text-brand-primary" />
              </div>
              <p className="font-heading text-2xl font-bold text-brand-text-primary">{fmtMoney(positions?.total_value || 0)}</p>
              <p className="text-[10px] text-brand-text-secondary mt-1">{positions?.positions?.length || 0} open positions</p>
            </CardContent>
          </Card>
          <Card className="border border-brand-border bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Invested</p>
                <span className="text-xs font-mono text-brand-text-secondary">$</span>
              </div>
              <p className="font-heading text-2xl font-bold text-brand-text-primary">{fmtMoney(positions?.total_invested || 0)}</p>
              <p className="text-[10px] text-brand-text-secondary mt-1">Capital deployed</p>
            </CardContent>
          </Card>
          <Card className={`border ${(positions?.total_pnl || 0) >= 0 ? 'border-brand-success/30 bg-brand-success/5' : 'border-brand-error/30 bg-brand-error/5'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Unrealized P&L</p>
                {(positions?.total_pnl || 0) >= 0 ? <ArrowUpRight size={16} className="text-brand-success" /> : <ArrowDownRight size={16} className="text-brand-error" />}
              </div>
              <p className={`font-heading text-2xl font-bold ${(positions?.total_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                {(positions?.total_pnl || 0) >= 0 ? '+' : ''}{fmtMoney(positions?.total_pnl || 0)}
              </p>
              <p className={`text-[10px] mt-1 font-semibold ${(positions?.total_pnl_pct || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                {(positions?.total_pnl_pct || 0) >= 0 ? '+' : ''}{positions?.total_pnl_pct || 0}% all-time
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main trading grid */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          {/* ── Asset List ── */}
          <div className="xl:col-span-1 space-y-3">
            <Card className="border border-brand-border bg-white">
              <CardContent className="p-3 space-y-3">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search asset…" className="pl-8 h-8 text-xs bg-brand-bg/50 border-brand-border rounded-lg" data-testid="asset-search-input" />
                </div>
                <Tabs defaultValue="all">
                  <TabsList className="bg-brand-bg/50 border border-brand-border/50 rounded-lg p-0.5 w-full grid grid-cols-3">
                    <TabsTrigger value="all" className="rounded-md text-[10px] data-[state=active]:bg-white data-[state=active]:text-brand-primary">Favs</TabsTrigger>
                    <TabsTrigger value="crypto" className="rounded-md text-[10px] data-[state=active]:bg-white data-[state=active]:text-brand-primary">Crypto</TabsTrigger>
                    <TabsTrigger value="commodity" className="rounded-md text-[10px] data-[state=active]:bg-white data-[state=active]:text-brand-primary">Comm.</TabsTrigger>
                  </TabsList>
                  {[['all', favAssets], ['crypto', cryptoAssets], ['commodity', commodityAssets]].map(([key, list]) => (
                    <TabsContent key={key} value={key} className="mt-2 space-y-0.5 max-h-[520px] overflow-y-auto pr-1">
                      {list.length === 0 ? (
                        <p className="text-center text-[11px] text-brand-text-secondary py-6">{key === 'all' ? 'No favorites yet. Tap ★ to add.' : 'No assets'}</p>
                      ) : list.map(a => {
                        const p = prices[a.symbol] || {};
                        const isSelected = selectedSymbol === a.symbol;
                        const isFav = favs.includes(a.symbol);
                        return (
                          <div key={a.symbol}
                            onClick={() => setSelectedSymbol(a.symbol)}
                            className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all border ${isSelected ? 'bg-brand-primary/10 border-brand-primary/40' : 'hover:bg-brand-bg border-transparent'}`}
                            data-testid={`asset-${a.symbol}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <button onClick={(e) => { e.stopPropagation(); toggleFav(a.symbol); }} className="flex-shrink-0">
                                <Star size={11} className={isFav ? 'text-yellow-500 fill-yellow-500' : 'text-brand-text-secondary/40 group-hover:text-brand-text-secondary'} />
                              </button>
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-brand-text-primary leading-none">{a.symbol}</p>
                                <p className="text-[10px] text-brand-text-secondary truncate mt-0.5">{a.name}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs font-mono font-bold text-brand-text-primary leading-none">${fmtPrice(p.price)}</p>
                              <p className={`text-[10px] font-semibold mt-0.5 ${(p.change_24h || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                                {(p.change_24h || 0) >= 0 ? '+' : ''}{(p.change_24h || 0).toFixed(2)}%
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* ── Chart + Order Panel ── */}
          <div className="xl:col-span-3 space-y-4">
            {/* Price header */}
            <Card className="border border-brand-border bg-white">
              <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-5">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-heading text-xl font-bold text-brand-text-primary">{selectedSymbol}</h2>
                      <button onClick={() => toggleFav(selectedSymbol)} className="text-brand-text-secondary hover:text-yellow-500"><Star size={14} className={favs.includes(selectedSymbol) ? 'text-yellow-500 fill-yellow-500' : ''} /></button>
                    </div>
                    <p className="text-[11px] text-brand-text-secondary">{assets.find(a => a.symbol === selectedSymbol)?.name}</p>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-brand-text-primary tabular-nums">${fmtPrice(currentPrice)}</p>
                    <Badge className={`text-xs border-0 mt-1 ${change24h >= 0 ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>
                      {change24h >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />} {change24h >= 0 ? '+' : ''}{change24h.toFixed(2)}% (24h)
                    </Badge>
                  </div>
                </div>
                {/* Quick Trade Bar */}
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex border border-brand-border rounded-lg overflow-hidden h-9">
                    <button onClick={() => setTradeForm({ ...tradeForm, side: 'buy' })}
                      className={`px-5 text-xs font-bold transition-colors ${tradeForm.side === 'buy' ? 'bg-brand-success text-white' : 'bg-white text-brand-text-secondary hover:bg-brand-bg'}`} data-testid="buy-btn">BUY</button>
                    <button onClick={() => setTradeForm({ ...tradeForm, side: 'sell' })}
                      className={`px-5 text-xs font-bold transition-colors ${tradeForm.side === 'sell' ? 'bg-brand-error text-white' : 'bg-white text-brand-text-secondary hover:bg-brand-bg'}`} data-testid="sell-btn">SELL</button>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[9px] uppercase tracking-wider text-brand-text-secondary font-semibold mb-1">Quantity</label>
                    <Input type="number" step="any" value={tradeForm.quantity} onChange={e => setTradeForm({ ...tradeForm, quantity: e.target.value })}
                      placeholder="0.00" className="w-28 bg-white border-brand-border rounded-lg h-9 text-sm font-mono" data-testid="trade-qty-input" />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[9px] uppercase tracking-wider text-brand-text-secondary font-semibold mb-1">Est. Total</label>
                    <div className={`h-9 px-3 flex items-center text-xs font-mono font-bold rounded-lg border ${!canAfford ? 'border-brand-error/40 bg-brand-error/5 text-brand-error' : 'border-brand-border bg-brand-bg/40 text-brand-text-primary'}`}>
                      {fmtMoney(tradeValue)}
                    </div>
                  </div>
                  <Button onClick={handleTrade} disabled={executing || !canAfford || qty <= 0}
                    className={`rounded-lg text-xs font-bold h-9 px-6 ${tradeForm.side === 'buy' ? 'bg-brand-success hover:bg-brand-success/90' : 'bg-brand-error hover:bg-brand-error/90'} text-white disabled:opacity-50`}
                    data-testid="execute-trade-btn">
                    {executing ? <Loader2 size={14} className="animate-spin" /> : `${tradeForm.side === 'buy' ? 'BUY' : 'SELL'} ${selectedSymbol}`}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* TradingView Chart */}
            <Card className="border border-brand-border bg-white overflow-hidden">
              <CardContent className="p-0">
                <div className="relative" style={{ height: '460px', width: '100%' }}>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-brand-text-secondary text-xs z-0">
                    <Loader2 size={14} className="animate-spin mr-2" /> Loading chart…
                  </div>
                  <div ref={chartRef} className="tradingview-widget-container relative z-10" style={{ height: '460px', width: '100%' }} data-testid="tradingview-chart" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Positions & History */}
        <Tabs defaultValue="positions" className="mt-4">
          <TabsList className="bg-brand-bg/50 border border-brand-border/50 rounded-lg p-0.5">
            <TabsTrigger value="positions" className="rounded-md text-xs px-5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-brand-primary">Open Positions {positions?.positions?.length ? `(${positions.positions.length})` : ''}</TabsTrigger>
            <TabsTrigger value="history" className="rounded-md text-xs px-5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-brand-primary">Trade History</TabsTrigger>
          </TabsList>

          <TabsContent value="positions" className="mt-3">
            <Card className="border border-brand-border bg-white">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="positions-table">
                    <thead><tr className="border-b border-brand-border bg-brand-bg/40">
                      {['Asset', 'Qty', 'Avg Price', 'Current', 'Invested', 'Value', 'P&L', 'P&L %', 'Action'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {(!positions?.positions || positions.positions.length === 0) ? (
                        <tr><td colSpan={9} className="py-12 text-center text-brand-text-secondary text-sm">No open positions — execute a trade above to get started.</td></tr>
                      ) : positions.positions.map(p => (
                        <tr key={p.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                          <td className="py-2.5 px-3">
                            <span className="font-bold text-brand-text-primary text-xs">{p.symbol}</span>
                            <p className="text-[10px] text-brand-text-secondary">{p.asset_name}</p>
                          </td>
                          <td className="py-2.5 px-3 text-xs font-mono">{p.quantity}</td>
                          <td className="py-2.5 px-3 text-xs font-mono">${fmtPrice(p.avg_price)}</td>
                          <td className="py-2.5 px-3 text-xs font-mono font-bold">${fmtPrice(p.current_price)}</td>
                          <td className="py-2.5 px-3 text-xs">{fmtMoney(p.invested_amount)}</td>
                          <td className="py-2.5 px-3 text-xs font-bold">{fmtMoney(p.current_value)}</td>
                          <td className={`py-2.5 px-3 text-xs font-bold ${(p.unrealized_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                            {(p.unrealized_pnl || 0) >= 0 ? '+' : ''}{fmtMoney(p.unrealized_pnl)}
                          </td>
                          <td className={`py-2.5 px-3 text-xs font-bold ${(p.pnl_pct || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                            {(p.pnl_pct || 0) >= 0 ? '+' : ''}{p.pnl_pct}%
                          </td>
                          <td className="py-2.5 px-3">
                            <Button size="sm" variant="outline" disabled={executing}
                              className="h-7 text-[10px] rounded text-brand-error border-brand-error/30 hover:bg-brand-error/10"
                              onClick={() => closePosition(p)} data-testid={`close-${p.symbol}`}>Close</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-3">
            <Card className="border border-brand-border bg-white">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="trade-history-table">
                    <thead><tr className="border-b border-brand-border bg-brand-bg/40">
                      {['Trade ID', 'Asset', 'Side', 'Qty', 'Price', 'Value', 'P&L', 'Time'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {tradeHistory.length === 0 ? (
                        <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary text-sm">No trades yet</td></tr>
                      ) : tradeHistory.map(t => (
                        <tr key={t.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover">
                          <td className="py-2.5 px-3 text-[10px] font-mono text-brand-primary">{t.trade_id}</td>
                          <td className="py-2.5 px-3 text-xs font-bold">{t.symbol}</td>
                          <td className="py-2.5 px-3"><Badge className={`text-[10px] border-0 ${t.side === 'buy' ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>{t.side?.toUpperCase()}</Badge></td>
                          <td className="py-2.5 px-3 text-xs font-mono">{t.quantity}</td>
                          <td className="py-2.5 px-3 text-xs font-mono">${fmtPrice(t.entry_price)}</td>
                          <td className="py-2.5 px-3 text-xs">{fmtMoney(t.total_value)}</td>
                          <td className={`py-2.5 px-3 text-xs font-bold ${(t.realized_pnl || 0) >= 0 ? 'text-brand-success' : t.realized_pnl ? 'text-brand-error' : 'text-brand-text-secondary'}`}>
                            {t.realized_pnl != null ? `${t.realized_pnl >= 0 ? '+' : ''}${fmtMoney(t.realized_pnl)}` : '—'}
                          </td>
                          <td className="py-2.5 px-3 text-[10px] text-brand-text-secondary">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
